/**
 * Shared helpers for the Supabase repository implementations.
 *
 * Hard rules enforced here:
 * - user identity ALWAYS comes from the active Supabase session
 *   (`requireSupabase().auth.getUser()`), never from caller-supplied ids;
 * - every method throws a typed AppError via `toAppError` — raw PostgREST /
 *   Storage errors never escape a repository;
 * - a missing/invalid session throws AUTH_SESSION_INVALID, not a fake success.
 */
import { requireSupabase } from '@/lib/supabase/client'
import { makeAppError, newCorrelationId, toAppError } from '@/lib/supabase/errors'
import type { AppError, AppErrorSource } from '@/types'

/** Returns the authenticated user's id or throws a typed session error. */
export async function requireUserId(): Promise<string> {
  const supabase = requireSupabase()
  try {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    if (!data.user) {
      throw makeAppError({
        code: 'AUTH_SESSION_INVALID',
        userMessage: 'Je sessie is verlopen. Log opnieuw in.',
        technicalMessage: 'auth.getUser() returned no user',
        retryable: false,
        source: 'auth',
        correlationId: newCorrelationId(),
      })
    }
    return data.user.id
  } catch (error) {
    throw toAppError(error, 'auth')
  }
}

/** Maps an unknown throw to AppError for the given source. */
export function fail(error: unknown, source: AppErrorSource = 'database'): never {
  throw toAppError(error, source)
}

/** Typed validation error factory (Dutch, user-safe copy). */
export function validationError(code: string, userMessage: string, technicalMessage?: string): AppError {
  return makeAppError({
    code,
    userMessage,
    technicalMessage,
    retryable: false,
    source: 'validation',
    correlationId: newCorrelationId(),
  })
}

/** Typed "not found / no access" error (RLS-invisible rows look the same). */
export function notFoundError(userMessage = 'Dit item bestaat niet of je hebt er geen toegang toe.'): AppError {
  return makeAppError({
    code: 'NOT_FOUND',
    userMessage,
    retryable: false,
    source: 'database',
    correlationId: newCorrelationId(),
  })
}

/**
 * Throws when a `.select().single()`-style write returned no row. PostgREST
 * signals this with PGRST116; both shapes map to the same honest error.
 */
export function assertRowReturned<T>(data: T | null, error: unknown): T {
  if (error) fail(error)
  if (data === null || data === undefined) fail(notFoundError())
  return data as T
}

export function newObjectId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `o_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}
